import { useEffect, useState } from 'react';
import { LANGUAGE_CN, LANGUAGE_EN } from '@home/_config/config';
import { LANGUAGES, extractTextFromResponse } from '@intelligence/pages/LowCodeTemplate/utils';

import type { ILanguage } from '@intelligence/pages/LowCodeTemplate/utils';

type FetchObjectParams = {
  key: string;
  responseType?: 'text' | 'arraybuffer' | 'blob';
};

type GetObjectFn = (params: FetchObjectParams) => Promise<unknown>;

type GetObjectUrlFn = (params: { key: string }) => Promise<{ url: string }>;

interface IUseLowcodeCosTemplateParams {
  templateConfig: IRecord;
  locale: string;
  isCOSInited: boolean;
  getObject: GetObjectFn;
  getObjectUrl: GetObjectUrlFn;
}

interface IUseLowcodeCosTemplateResult {
  loading: boolean;
  error?: unknown;
  currentLanguage: ILanguage;
  displayLanguage: ILanguage;
  displayHtmlContent: string;
  displayJsonData: unknown;
}

const TEMPLATE_PATH_KEY_MAP: Record<ILanguage, string> = {
  [LANGUAGE_EN]: 'en_templatePath',
  [LANGUAGE_CN]: 'zh_templatePath',
};

const DATA_PATH_KEY_MAP: Record<ILanguage, string> = {
  [LANGUAGE_EN]: 'en_dataPath',
  [LANGUAGE_CN]: 'zh_dataPath',
};

const createEmptyHtmlContentMap = (): Record<ILanguage, string> => {
  return {
    [LANGUAGE_EN]: '',
    [LANGUAGE_CN]: '',
  };
};

const getCurrentLanguage = (locale: string): ILanguage => {
  if (locale === LANGUAGE_CN) {
    return LANGUAGE_CN;
  }

  return LANGUAGE_EN;
};

const getFallbackLanguage = (language: ILanguage): ILanguage => {
  if (language === LANGUAGE_CN) {
    return LANGUAGE_EN;
  }

  return LANGUAGE_CN;
};

const isLanguageAvailable = (
  language: ILanguage,
  config: IRecord | undefined,
  htmlContentMap: Record<ILanguage, string>,
  jsonDataMap: Partial<Record<ILanguage, unknown>>,
): boolean => {
  const templatePathKey = TEMPLATE_PATH_KEY_MAP[language];
  const dataPathKey = DATA_PATH_KEY_MAP[language];
  const templatePath = config?.[templatePathKey] as string | undefined;
  const dataPath = config?.[dataPathKey] as string | undefined;
  const htmlContent = htmlContentMap[language];
  const jsonData = jsonDataMap[language];

  if (!templatePath || !htmlContent) {
    return false;
  }

  if (dataPath && typeof jsonData === 'undefined') {
    return false;
  }

  return true;
};

const getDisplayLanguage = (
  currentLanguage: ILanguage,
  config: IRecord | undefined,
  htmlContentMap: Record<ILanguage, string>,
  jsonDataMap: Partial<Record<ILanguage, unknown>>,
): ILanguage => {
  if (isLanguageAvailable(currentLanguage, config, htmlContentMap, jsonDataMap)) {
    return currentLanguage;
  }

  return getFallbackLanguage(currentLanguage);
};

export default function useLowcodeCosTemplate(props: IUseLowcodeCosTemplateParams): IUseLowcodeCosTemplateResult {
  const { templateConfig, locale, isCOSInited, getObject, getObjectUrl } = props;

  const currentLanguage = getCurrentLanguage(locale);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [htmlContentMap, setHtmlContentMap] = useState<Record<ILanguage, string>>(createEmptyHtmlContentMap);
  const [jsonDataMap, setJsonDataMap] = useState<Partial<Record<ILanguage, unknown>>>({});

  const fetchLanguageAssets = async (config?: IRecord) => {
    setLoading(true);
    setError(null);

    const nextHtmlContentMap = createEmptyHtmlContentMap();
    const nextJsonDataMap: Partial<Record<ILanguage, unknown>> = {};
    const errorMap: Partial<Record<ILanguage, Error[]>> = {};

    await Promise.all(
      LANGUAGES.map(async (language) => {
        const languageErrors: Error[] = [];
        const templatePathKey = TEMPLATE_PATH_KEY_MAP[language];
        const dataPathKey = DATA_PATH_KEY_MAP[language];
        const templatePath = config?.[templatePathKey] as string | undefined;
        const dataPath = config?.[dataPathKey] as string | undefined;
        const tasks: Array<Promise<void>> = [];

        if (templatePath) {
          tasks.push(
            (async () => {
              try {
                const body = await getObject({
                  key: templatePath,
                  responseType: 'text',
                });
                nextHtmlContentMap[language] = await extractTextFromResponse(body);
              } catch (fetchError) {
                languageErrors.push(fetchError instanceof Error ? fetchError : new Error(String(fetchError)));
              }
            })(),
          );
        }

        if (dataPath) {
          tasks.push(
            (async () => {
              try {
                const { url } = await getObjectUrl({ key: dataPath });
                const response = await fetch(url);

                if (!response.ok) {
                  throw new Error(`Failed to fetch JSON data for ${language}: ${response.statusText}`);
                }

                nextJsonDataMap[language] = await response.json();
              } catch (fetchError) {
                languageErrors.push(fetchError instanceof Error ? fetchError : new Error(String(fetchError)));
              }
            })(),
          );
        }

        await Promise.all(tasks);

        if (languageErrors.length > 0) {
          errorMap[language] = languageErrors;
        }
      }),
    );

    let hasAvailableLanguage = false;
    let firstError: Error | null = null;

    LANGUAGES.forEach((language) => {
      if (isLanguageAvailable(language, config, nextHtmlContentMap, nextJsonDataMap)) {
        hasAvailableLanguage = true;
      }

      if (!firstError && errorMap[language]?.[0]) {
        firstError = errorMap[language]?.[0] || null;
      }
    });

    if (!hasAvailableLanguage) {
      setError(firstError || new Error('No available low-code template content found.'));
    }

    setHtmlContentMap(nextHtmlContentMap);
    setJsonDataMap(nextJsonDataMap);
    setLoading(false);
  };

  useEffect(() => {
    if (!isCOSInited || !templateConfig) {
      if (!templateConfig) {
        setLoading(false);
      }
      return;
    }

    void fetchLanguageAssets(templateConfig);
  }, [isCOSInited, templateConfig]);

  const displayLanguage = getDisplayLanguage(currentLanguage, templateConfig, htmlContentMap, jsonDataMap);
  const displayHtmlContent = htmlContentMap[displayLanguage];
  const displayJsonData = jsonDataMap[displayLanguage];
  const hasDisplayLanguage = isLanguageAvailable(displayLanguage, templateConfig, htmlContentMap, jsonDataMap);
  const hasCurrentLanguage = isLanguageAvailable(currentLanguage, templateConfig, htmlContentMap, jsonDataMap);
  const fallbackLanguage = getFallbackLanguage(currentLanguage);
  const hasFallbackLanguage = isLanguageAvailable(fallbackLanguage, templateConfig, htmlContentMap, jsonDataMap);
  const displayError =
    !loading && !hasDisplayLanguage && !hasCurrentLanguage && !hasFallbackLanguage
      ? error || new Error('No available low-code template content found.')
      : undefined;

  return {
    loading,
    error: displayError,
    currentLanguage,
    displayLanguage,
    displayHtmlContent,
    displayJsonData,
  };
}
